import React, { useEffect, useState } from 'react';
import styles from './CreateEvaluationQuestion.module.scss';
import Breadcrumbs from '../../../components/breadcrumbs/Breadcrumbs';
import Button from '../../../components/button/Button';
import TextAreaField from '../../../components/textAreaField/TextAreaField';
import Dropdown from '../../../components/dropdown/Dropdown';
import MultiSelectDropdown from '../../../components/multiSelectDropdown/MultiSelectDropdown';
import LoadingOverlay from '../../../components/loadingOverlay/LoadingOverlay';
import axiosClient from '../../../api/axiosClient';
import { useToasts } from '../../../contexts/ToastContext';
import { useNavigate, useParams } from 'react-router-dom';

const CreateEvaluationQuestion = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [allTypes, setAllTypes] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [allSpecialities, setAllSpecialities] = useState([]);
  const [formData, setFormData] = useState({
    question: '',
    type: '',
    tags: [],
    specialityIds: [],
    year: '',
  });

  useEffect(() => {
    if (!questionId) return;
    const fetchQuestion = async () => {
      setIsLoading(true);
      try {
        const response = await axiosClient.get(`/evaluation-questions/${questionId}`);
        const data = response.data;
        setFormData({
          question: data.question || '',
          type: data.type || '',
          tags: data.tags || [],
          specialityIds: data.specialities?.map((s) => s.id) || [],
          year: data.year?.year || '',
        });
        setAllTypes(data.allTypes?.map((t) => ({ value: t, label: t })) || []);
        setAllTags(data.allTags || []);
        setAllSpecialities(data.allSpecialities?.map((s) => ({ value: s.id, label: s.name })) || []);
      } catch (error) {
        showToast(error.message, 'error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchQuestion();
  }, [questionId, showToast]);

  const handleSave = async () => {
    if (!formData.question || !formData.type) {
      showToast('Question and type are required', 'error');
      return;
    }
    setIsSaving(true);
    try {
      await axiosClient.patch(`/evaluation-questions/${questionId}`, {
        question: formData.question,
        type: formData.type,
        tags: formData.tags,
        specialityIds: formData.specialityIds,
      });
      showToast('Question updated successfully', 'success');
      navigate('/evaluation-questions');
    } catch (error) {
      showToast(error.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Evaluation Questions', path: '/evaluation-questions' },
            { label: 'Edit Question' },
          ]}
        />
      </div>
      <div className={styles.container}>
        {(isLoading || isSaving) && <LoadingOverlay />}
        <div className={styles.form}>
          <div className={styles.row}>
            <div className={styles.column}>
              <TextAreaField
                label="Question"
                value={formData.question}
                onChange={(value) => setFormData((prev) => ({ ...prev, question: value }))}
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.column}>
              <Dropdown
                label="Type"
                options={allTypes}
                value={formData.type}
                onChange={(value) => setFormData((prev) => ({ ...prev, type: value }))}
              />
            </div>
            <div className={styles.column}>
              <Dropdown
                label="Year"
                options={[{ value: formData.year, label: formData.year }]}
                value={formData.year}
                onChange={() => {}}
                disabled
              />
            </div>
          </div>
          <div className={styles.row}>
            <div className={styles.column}>
              <MultiSelectDropdown
                label="Tags"
                options={allTags.map((t) => ({ value: t, label: t }))}
                selected={formData.tags}
                onChange={(values) => setFormData((prev) => ({ ...prev, tags: values }))}
              />
            </div>
            <div className={styles.column}>
              <MultiSelectDropdown
                label="Specialities"
                options={allSpecialities}
                selected={formData.specialityIds}
                onChange={(values) => setFormData((prev) => ({ ...prev, specialityIds: values }))}
              />
            </div>
          </div>
          <div className={styles.actions}>
            <Button
              label="Cancel"
              variant="white"
              size="medium"
              onClick={() => navigate('/evaluation-questions')}
            />
            <Button
              label="Save"
              variant="primary"
              size="medium"
              isLoading={isSaving}
              onClick={handleSave}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateEvaluationQuestion;
